import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Space,
  Popconfirm,
  Tag,
  Card,
  Pagination,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import { usePermissions } from '../../../context/permissionsContext';

const { TextArea } = Input;

const Categorias = () => {
  const { hasPermission } = usePermissions();
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState(null);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [filtroNombre, setFiltroNombre] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const loadCategorias = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/gestion/categorias-articulos');
      if (response.ok) {
        const data = await response.json();
        setCategorias(data);
        setPagination(prev => ({ ...prev, total: data.length }));
      } else {
        message.error('Error al cargar las categorías');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar las categorías');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategorias();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const categoriasFiltradas = categorias.filter(cat =>
    !filtroNombre || cat.cat_art_nombre?.toLowerCase().includes(filtroNombre.toLowerCase())
  );

  const categoriasPaginadas = categoriasFiltradas.slice(
    (pagination.current - 1) * pagination.pageSize,
    pagination.current * pagination.pageSize
  );

  const handleOpenModal = (categoria = null) => {
    setEditingCategoria(categoria);
    if (categoria) {
      form.setFieldsValue({
        nombre: categoria.cat_art_nombre,
        descripcion: categoria.cat_art_descripcion,
        activo: categoria.cat_art_activo,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ activo: true });
    }
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingCategoria) {
        const response = await apiClient.put(
          `/gestion/categorias-articulos/${editingCategoria.cat_art_id}`,
          values
        );
        if (response.ok) {
          message.success('Categoría actualizada correctamente');
          setModalVisible(false);
          loadCategorias();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al actualizar la categoría');
        }
      } else {
        const response = await apiClient.post('/gestion/categorias-articulos', values);
        if (response.ok) {
          message.success('Categoría creada correctamente');
          setModalVisible(false);
          loadCategorias();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al crear la categoría');
        }
      }
    } catch (error) {
      if (error.errorFields) return;
      console.error('Error:', error);
      message.error('Error al guardar la categoría');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/gestion/categorias-articulos/${id}`);
      if (response.ok) {
        message.success('Categoría eliminada correctamente');
        loadCategorias();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar la categoría');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar la categoría');
    }
  };

  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'cat_art_nombre',
      key: 'cat_art_nombre',
      render: (nombre) => (
        <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
          {nombre}
        </Tag>
      ),
    },
    {
      title: 'Descripción',
      dataIndex: 'cat_art_descripcion',
      key: 'cat_art_descripcion',
      render: (descripcion) => descripcion || '-',
    },
    {
      title: 'Cantidad de Artículos',
      dataIndex: 'cantidad_articulos',
      key: 'cantidad_articulos',
      render: (cantidad) => (
        <span style={{ fontWeight: 'bold' }}>{cantidad || 0}</span>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'cat_art_activo',
      key: 'cat_art_activo',
      render: (activo) => (
        <Tag color={activo ? 'green' : 'red'}>
          {activo ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    ...((hasPermission('categorias_articulos', 'editar') || hasPermission('categorias_articulos', 'eliminar')) ? [{
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      render: (_, record) => (
        <Space>
          {hasPermission('categorias_articulos', 'editar') && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleOpenModal(record)}
            >
              Editar
            </Button>
          )}
          {hasPermission('categorias_articulos', 'eliminar') && (
            <Popconfirm
              title="¿Estás seguro de eliminar esta categoría?"
              description={record.cantidad_articulos > 0 ? 
                `Esta categoría tiene ${record.cantidad_articulos} artículo(s) asignado(s). No se puede eliminar.` : 
                'Esta acción no se puede deshacer'}
              onConfirm={() => handleDelete(record.cat_art_id)}
              okText="Sí"
              cancelText="No"
              disabled={record.cantidad_articulos > 0}
            >
              <Button 
                type="link" 
                danger 
                icon={<DeleteOutlined />}
                disabled={record.cantidad_articulos > 0}
              >
                Eliminar
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    }] : []),
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#ffffff', fontSize: isMobile ? '20px' : '24px' }}>
            Categorías de Artículos
          </h1>
          <Space wrap style={{ marginBottom: 16 }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadCategorias}
              loading={loading}
            >
              {isMobile ? null : 'Actualizar'}
            </Button>
            {hasPermission('categorias_articulos', 'crear') && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleOpenModal(null)}
              >
                Nueva Categoría
              </Button>
            )}
          </Space>

          <div style={{
            marginBottom: 16,
            padding: 16,
            backgroundColor: '#2d2d2d',
            borderRadius: 8,
          }}>
            <Input
              placeholder="Buscar categoría por nombre..."
              value={filtroNombre}
              onChange={(e) => {
                setFiltroNombre(e.target.value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              allowClear
              style={{ maxWidth: 400 }}
            />
          </div>
        </div>

        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {categoriasPaginadas.map(categoria => (
              <Card
                key={categoria.cat_art_id}
                hoverable
                style={{ borderLeft: '4px solid #1890ff', borderRadius: '8px' }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <Tag color="blue" style={{ fontSize: '16px', padding: '4px 12px', marginBottom: '8px' }}>
                      {categoria.cat_art_nombre}
                    </Tag>
                    <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                      {categoria.cat_art_descripcion || 'Sin descripción'}
                    </div>
                    <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                      Artículos: {categoria.cantidad_articulos || 0}
                    </div>
                  </div>
                  {(hasPermission('categorias_articulos', 'editar') || hasPermission('categorias_articulos', 'eliminar')) && (
                    <Space wrap>
                      {hasPermission('categorias_articulos', 'editar') && (
                        <Button
                          type="link"
                          icon={<EditOutlined />}
                          onClick={() => handleOpenModal(categoria)}
                        >
                          Editar
                        </Button>
                      )}
                      {hasPermission('categorias_articulos', 'eliminar') && (
                        <Popconfirm
                          title="¿Estás seguro de eliminar esta categoría?"
                          description={categoria.cantidad_articulos > 0 ? 
                            `Esta categoría tiene ${categoria.cantidad_articulos} artículo(s) asignado(s). No se puede eliminar.` : 
                            'Esta acción no se puede deshacer'}
                          onConfirm={() => handleDelete(categoria.cat_art_id)}
                          okText="Sí"
                          cancelText="No"
                          disabled={categoria.cantidad_articulos > 0}
                        >
                          <Button 
                            type="link" 
                            danger 
                            icon={<DeleteOutlined />}
                            disabled={categoria.cantidad_articulos > 0}
                          >
                            Eliminar
                          </Button>
                        </Popconfirm>
                      )}
                    </Space>
                  )}
                </div>
              </Card>
            ))}
            {categoriasFiltradas.length > 0 && (
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={categoriasFiltradas.length}
                  onChange={(page, pageSize) => {
                    setPagination(prev => ({ ...prev, current: page, pageSize }));
                  }}
                  showSizeChanger
                  pageSizeOptions={['10', '20', '50', '100']}
                  showTotal={(total) => `Total: ${total} categorías`}
                />
              </div>
            )}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={categoriasPaginadas}
            loading={loading}
            rowKey="cat_art_id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: categoriasFiltradas.length,
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} categorías`,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => {
                setPagination(prev => ({ ...prev, current: page, pageSize }));
              },
            }}
          />
        )}

        {/* Modal para crear/editar categoría */}
        <Modal
          title={editingCategoria ? 'Editar Categoría' : 'Nueva Categoría'}
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          okText="Guardar"
          cancelText="Cancelar"
          width={600}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="nombre"
              label="Nombre"
              rules={[
                { required: true, message: 'El nombre es requerido' },
                { max: 100, message: 'El nombre no puede exceder 100 caracteres' },
              ]}
            >
              <Input placeholder="Nombre de la categoría" />
            </Form.Item>

            <Form.Item
              name="descripcion"
              label="Descripción"
              rules={[
                { max: 500, message: 'La descripción no puede exceder 500 caracteres' },
              ]}
            >
              <TextArea rows={3} placeholder="Descripción de la categoría (opcional)" />
            </Form.Item>

            <Form.Item
              name="activo"
              label="Estado"
              valuePropName="checked"
            >
              <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default Categorias;
